import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { App as AntdApp } from "antd";

import { ROUTE_REDIRECTS } from "../navigation";

/**
 * Terminal route for anything the router does not recognise.
 *
 * Without it React Router matches nothing and renders `null`, so an unknown URL
 * produces a blank white page with no indication of what went wrong — which is
 * where every stale bookmark has been landing.
 *
 * A renamed route resolves through `ROUTE_REDIRECTS` to the page it actually
 * meant. Falling back to the landing page is the backstop for genuinely unknown
 * URLs, not the answer for a rename: it keeps the user oriented but loses their
 * destination, so a rename without a map entry is a bug the guard test catches.
 *
 * The notice is deliberate. A silent bounce hides the typo, leaving the user to
 * wonder why they are somewhere they did not ask for.
 */
const NotFoundRedirect = () => {
  const location = useLocation();
  const { message } = AntdApp.useApp();

  // hasOwn, not a bare index: the key is user-controlled, and a plain object
  // would resolve `constructor` or `__proto__` to something off the prototype
  // chain. Unreachable while pathnames are "/"-prefixed, but the map grows.
  const target = Object.hasOwn(ROUTE_REDIRECTS, location.pathname)
    ? ROUTE_REDIRECTS[location.pathname]
    : undefined;

  useEffect(() => {
    if (target) return;
    // Bounded: the path is untrusted input rendered straight into the UI. React
    // escapes it, so this is about an unbounded string, not injection.
    const shown = location.pathname.slice(0, 80);
    message.info(`No page at ${shown} — showing the home page instead.`);
  }, [target, location.pathname, message]);

  return (
    <Navigate
      to={target ?? "/"}
      replace
    />
  );
};

export default NotFoundRedirect;
